import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { ISettingsController } from "by/gamefactory/cityversetycoon/settings/ISettingsController";
import { ISettingsModel } from "by/gamefactory/cityversetycoon/settings/ISettingsModel";
import { gsap } from "gsap";
import { Container, IDestroyOptions, Point, Rectangle, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { nameof } from "../../../../../../shared/bindable-data";
import { Web3Model } from "../../../../../../web3/models/Web3Model";
import { SceneIndex } from "../../../../SceneIndex";
import { BaseScalableView } from "../../../../common/BaseScalableView";
import { GameFont } from "../../../../common/GameFont";
import { Avatar } from "../../../../common/components/Avatar";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import { ITextInput } from "../../../../common/components/input/ITextInput";
import { textInputFactory } from "../../../../common/components/input/TextInputFactory";
import { HBox } from "../../../../common/components/ui/HBox";
import { IGuestModel } from "../../../../common/models/IGuestModel";
import { IPlayerModel } from "../../../../common/models/IPlayerModel";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../../common/resources/SoundsResourcePackageWrapper";
import { FullscreenInputBlocker } from "../../../../game/components/FullscreenInputBlocker";
import { TextWithUnderline } from "../../../../game/components/TextWithUnderline";
import { TopPanel } from "../../../../game/components/toppanel/TopPanel";
import { IGameController } from "../../../../game/controllers/IGameController";
import { IGameViewModel } from "../../../../game/models/IGameViewModel";
import { IUserLevelUpModel } from "../../../userlevelup/model/IUserLevelUpModel";
import { LevelProgressContainer } from "../../components/LevelProgressContainer";
import { FAQButton } from "../../components/buttons/FAQButton";
import { SocialButton } from "../../components/buttons/SocialButton";
import { StoreButton } from "../../components/buttons/StoreButton";
import { SupportButton } from "../../components/buttons/SupportButton";
import { VolumeButton } from "../../components/buttons/VolumeButton";
import { WalletButton } from "../../components/buttons/WalletButton";
import { IProfileController } from "../controllers/IProfileController";
import { UserProfileResourcePackage } from "../../resources/UserProfileResourcePackage";
import { UserProfileResult } from "../UserProfileResult";
import { GitBookPage } from "../../../../../../tools/Redirector";

export class UserProfilePopupView extends BaseScalableView<UserProfileResult> {
    private static readonly TERMS_OF_USE_TEXT = "TERMS OF USE";
    private static readonly PRIVACY_POLICY_TEXT = "PRIVACY POLICY";
    private static readonly COOKIES_POLICY_TEXT = "COOKIES POLICY";
    private static readonly COPY_USER_ID_TEXT = "COPY USER ID";
    private static readonly SIGN_OUT_TEXT = "SIGN OUT";

    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly profileController: IProfileController;
    private readonly guestModel: IGuestModel;
    private readonly playerModel: IPlayerModel;
    private readonly userLevelUpModel: IUserLevelUpModel;
    private readonly settingsModel: ISettingsModel;
    private readonly web3Model: Web3Model;
    private readonly settingsController: ISettingsController;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly gameViewModel: IGameViewModel;
    private readonly gameController: IGameController;
    private readonly logger: ILogger;

    private viewBackground: Sprite;
    private frame: Sprite;
    private levelProgressContainer: LevelProgressContainer;
    private closeButton: ButtonWithScaleAnimation;
    private leftButton: ButtonWithScaleAnimation;
    private rightButton: ButtonWithScaleAnimation;
    private editButton: ButtonWithScaleAnimation;
    private avatar: Avatar;
    private playerNickname: ITextInput;
    private userProfileContent: Container;
    private userNicknameBox: HBox;
    private walletButton: WalletButton;
    private faqButton: FAQButton;
    private supportButton: SupportButton;
    private storeButton: StoreButton;
    private socialButton: SocialButton;
    private volumeButton: VolumeButton;
    private underline: Sprite;
    private termsText: TextWithUnderline;
    private privacyPolicyText: TextWithUnderline;
    private cookiesPolicyText: TextWithUnderline;
    private copyUserIdText: TextWithUnderline;
    private signOutText: Text;
    private notificationText: Text;
    private inputBlocker: FullscreenInputBlocker;

    public constructor(
        operationControls: PromiseCompletionSource<UserProfileResult>,
        userProfileResources: UserProfileResourcePackage,
        commonResources: CommonResourcesPackage,
        canvasScaler: ICanvasScaler,
        profileController: IProfileController,
        guestModel: IGuestModel,
        playerModel: IPlayerModel,
        web3Model: Web3Model,
        userLevelUpModel: IUserLevelUpModel,
        soundResources: SoundsResourcePackageWrapper,
        settingsModel: ISettingsModel,
        settingsController: ISettingsController,
        gameViewModel: IGameViewModel,
        gameController: IGameController,
        logger: ILogger
    ) {
        super(operationControls, canvasScaler);
        this.userProfileResources = userProfileResources;
        this.commonResources = commonResources;
        this.profileController = profileController;
        this.guestModel = guestModel;
        this.playerModel = playerModel;
        this.web3Model = web3Model;
        this.userLevelUpModel = userLevelUpModel;
        this.soundResources = soundResources;
        this.settingsModel = settingsModel;
        this.settingsController = settingsController;
        this.gameViewModel = gameViewModel;
        this.gameController = gameController;
        this.logger = logger;
        this.blurHandle = this.blurHandle.bind(this);
        this.onInputStartEditing = this.onInputStartEditing.bind(this);
        this.onTextChanged = this.onTextChanged.bind(this);
        this.build();
        guestModel.propertyChanged.add(this.onGuestModelPropertyChanged, this);
        playerModel.propertyChanged.add(this.onPlayerModelPropertyChanged, this);
        window.onbeforeunload = () => { this.profileController.saveGuestProfile(); };
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public async start(): Promise<void> {
        await this.show();
    }

    private async onGuestModelPropertyChanged(model: IGuestModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "avatarTexture"):
                this.avatar.updateTexture(this.guestModel.avatarTexture.value);
                break;
        }
    }

    private async onPlayerModelPropertyChanged(model: IPlayerModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "lastLeaderboardPlace"):
                this.avatar.updateLeaderboardIcon(model.lastLeaderboardPlace.value);
                break;
        }
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";
        this.addChild(this.viewBackground);

        this.userProfileContent = new Container();
        this.addChild(this.userProfileContent);

        this.frame = new Sprite(this.userProfileResources.assets_views_user_profile_frame_png);
        this.frame.anchor.set(0.5);
        this.userProfileContent.addChild(this.frame);

        this.avatar = new Avatar(this.userProfileResources, this.guestModel.avatarTexture.value);
        this.avatar.enabled = false;
        this.avatar.avatarSize = new Point(220, 220);
        this.avatar.trophySize = new Point(117, 150);
        this.userProfileContent.addChild(this.avatar);

        this.leftButton = new ButtonWithScaleAnimation(this.userProfileResources.assets_views_user_profile_left_button_png);
        this.leftButton.hitArea = new Rectangle(-this.leftButton.width * 0.2, -this.leftButton.height * 0.2, this.leftButton.width * 1.2, this.leftButton.height * 1.3);
        this.leftButton.onUp.add(this.changeOnLastAvatar, this);
        this.userProfileContent.addChild(this.leftButton);

        this.rightButton = new ButtonWithScaleAnimation(this.userProfileResources.assets_views_user_profile_left_button_png);
        this.rightButton.hitArea = new Rectangle(-this.rightButton.width * 0.2, -this.rightButton.height * 0.2, this.rightButton.width * 1.2, this.rightButton.height * 1.3);
        this.rightButton.scale.x = -1;
        this.rightButton.onUp.add(this.changeOnNextAvatar, this);
        this.userProfileContent.addChild(this.rightButton);

        this.levelProgressContainer = new LevelProgressContainer(this.userProfileResources, this.playerModel, this.userLevelUpModel);
        this.userProfileContent.addChild(this.levelProgressContainer);

        this.userNicknameBox = new HBox(15);
        this.userProfileContent.addChild(this.userNicknameBox);

        const textInputBackground = new Sprite(Texture.EMPTY);
        textInputBackground.height = this.userProfileResources.assets_views_user_profile_edit_button_png.height;
        this.playerNickname = textInputFactory({ bg: textInputBackground, textStyle: new NickNameTextStyle(), align: "center", maxLength: 20 });
        this.playerNickname.onStartEditing.add(this.onInputStartEditing, this);
        this.playerNickname.onChange.connect(this.onTextChanged);
        this.playerNickname.onEnter.connect(this.blurHandle);
        this.playerNickname.eventMode = "none";
        this.userNicknameBox.addChild(this.playerNickname);

        this.editButton = new ButtonWithScaleAnimation(this.userProfileResources.assets_views_user_profile_edit_button_png);
        this.editButton.width = 37;
        this.editButton.height = 37;
        this.editButton.onUp.add(this.editButtonClickHandle, this);
        this.editButton.position.y = 3.5;
        this.userNicknameBox.addChild(this.editButton);

        this.walletButton = new WalletButton(this.userProfileResources, this.soundResources, this.logger);
        this.walletButton.onUp.add(this.onWalletClick, this);
        this.userProfileContent.addChild(this.walletButton);

        this.faqButton = new FAQButton(this.userProfileResources, this.soundResources, this.logger);
        this.faqButton.onUp.add(this.openGitbook, this);
        this.userProfileContent.addChild(this.faqButton);

        this.supportButton = new SupportButton(this.userProfileResources, this.soundResources, this.logger);
        this.supportButton.onUp.add(this.supportButtonClickHandle, this);
        this.userProfileContent.addChild(this.supportButton);

        this.storeButton = new StoreButton(this.userProfileResources, this.soundResources, this.logger);
        this.storeButton.onUp.add(this.storeClickHandle, this);
        this.userProfileContent.addChild(this.storeButton);

        this.socialButton = new SocialButton(this.userProfileResources, this.soundResources, this.logger);
        this.socialButton.onUp.add(this.socialClickHandle, this);
        this.userProfileContent.addChild(this.socialButton);

        this.volumeButton = new VolumeButton(this.userProfileResources, this.settingsModel, this.settingsController, this.soundResources, this.logger);
        this.userProfileContent.addChild(this.volumeButton);

        this.underline = new Sprite(this.userProfileResources.assets_views_user_profile_underline_png);
        this.userProfileContent.addChild(this.underline);

        this.termsText = new TextWithUnderline(UserProfilePopupView.TERMS_OF_USE_TEXT, new TextWithUnderlineTextStyle(), 0, -3);
        this.termsText.eventMode = "static";
        this.termsText.on("pointerup", this.openTermsOfUse, this);
        this.userProfileContent.addChild(this.termsText);

        this.privacyPolicyText = new TextWithUnderline(UserProfilePopupView.PRIVACY_POLICY_TEXT, new TextWithUnderlineTextStyle(), 0, -3);
        this.privacyPolicyText.eventMode = "static";
        this.privacyPolicyText.on("pointerup", this.openPrivacyPolicy, this);
        this.userProfileContent.addChild(this.privacyPolicyText);

        this.cookiesPolicyText = new TextWithUnderline(UserProfilePopupView.COOKIES_POLICY_TEXT, new TextWithUnderlineTextStyle(), 0, -3);
        this.cookiesPolicyText.eventMode = "static";
        this.cookiesPolicyText.on("pointerup", this.openCookiesPolicy, this);
        this.userProfileContent.addChild(this.cookiesPolicyText);

        this.copyUserIdText = new TextWithUnderline(UserProfilePopupView.COPY_USER_ID_TEXT, new TextWithUnderlineTextStyle(), 0, -3);
        this.copyUserIdText.eventMode = "static";
        this.copyUserIdText.addListener("pointerup", () => {
            window.FixedClipboard.copy(this.guestModel.id.value);
            this.showNotification();
        });
        this.userProfileContent.addChild(this.copyUserIdText);

        this.signOutText = new Text(UserProfilePopupView.SIGN_OUT_TEXT, new TextWithUnderlineTextStyle());
        this.signOutText.style.fill = "#005a4b";
        this.signOutText.eventMode = "static";
        this.signOutText.on("pointerup", this.signOutClickHandle, this);
        this.userProfileContent.addChild(this.signOutText);

        this.notificationText = new Text("User ID was copied!", new TextWithUnderlineTextStyle());
        this.notificationText.anchor.set(0.5);
        this.addChild(this.notificationText);

        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.5, -this.closeButton.height * 0.5, this.closeButton.width * 2, this.closeButton.height * 2);
        this.closeButton.onUp.add(this.hide, this);
        this.userProfileContent.addChild(this.closeButton);

        this.inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.inputBlocker.blocked = false;
        this.addChild(this.inputBlocker);
    }

    private async show(): Promise<void> {
        this.notificationText.alpha = 0;
        this.avatar.glow = this.playerModel.isAuthorized;
        this.avatar.updateLeaderboardIcon(this.playerModel.lastLeaderboardPlace.value);
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.fromTo(this.viewBackground, { alpha: 0 }, { alpha: 1, duration: 0.4, ease: "power1.in" });
        animationTimeLine.fromTo(this.userProfileContent, { width: 0, height: 0, angle: -30 }, { width: this.userProfileContent.width, height: this.userProfileContent.height, angle: 0, duration: 0.4, ease: "power1.in" }, "<");
        animationTimeLine.fromTo(this.userProfileContent, { x: this.canvasScaler.referenceWidth * 0.25, y: TopPanel.DEFAULT_POSITION_Y }, { x: 0, y: 0, duration: 0.4, ease: "power1.in" }, "<");
        this.setProfileInfo();
        await animationTimeLine;
        animationTimeLine.kill();
        if (this.userLevelUpModel != undefined) {
            this.inputBlocker.blocked = true;
            await this.levelProgressContainer.updateProgressAnimation();
            this.profileController.updatePlayer(this.userLevelUpModel.playerDto);
            this.userLevelUpModel.avatarPosition = this.avatar.position.clone();
            await this.profileController.showUserLevelUpPopup(this.userLevelUpModel);
            this.hide();
        }
    }

    private setProfileInfo(): void {
        this.avatar.updateTexture(this.guestModel.avatarTexture.value);
        this.playerNickname.value = this.guestModel.name.value;
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    private async hide(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.to(this.viewBackground, { alpha: 0, duration: 0.4, ease: "power1.out" });
        animationTimeLine.to(this.userProfileContent, { width: 0, height: 0, angle: -30, duration: 0.4, ease: "power1.out" }, "<");
        animationTimeLine.to(this.userProfileContent, { x: this.canvasScaler.referenceWidth * 0.25, y: TopPanel.DEFAULT_POSITION_Y, duration: 0.4, ease: "power1.out" }, "<");
        await animationTimeLine;
        animationTimeLine.kill();
        this.operationControls.complete(UserProfileResult.NONE);
    }

    private async changeOnNextAvatar(): Promise<void> {
        await this.profileController.changeOnNextAvatar();
    }

    private async changeOnLastAvatar(): Promise<void> {
        await this.profileController.changeOnLastAvatar();
    }

    private onInputStartEditing(): void {
        this.inputBlocker.blocked = true;
        this.editButton.visible = false;
        this.userNicknameBox.alignmentStrategy();
    }

    private editButtonClickHandle(): void {
        this.playerNickname.manualyStartEditing();
    }

    private blurHandle(): void {
        this.inputBlocker.blocked = false;
        this.editButton.visible = true;
        this.userNicknameBox.alignmentStrategy();
        this.profileController.changeName(this.playerNickname.value);
    }

    private onTextChanged(_text: string): void {
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    private async onWalletClick(): Promise<void> {
        if (!this.web3Model.connected.value) {
            this.logger.debug(`Web3 model not connected, unable to open myWallet.`);
            return;
        }
        this.operationControls.complete(UserProfileResult.SHOW_MY_WALLET);
    }

    private supportButtonClickHandle(): void {
        this.profileController.openIntercom();
    }

    private async socialClickHandle(): Promise<void> {
        this.gameViewModel.mainLoginShown.setValue(true);
        await Promise.all([this.hide(), this.gameController.showPopupBySceneIndex(SceneIndex.SOCIAL)]);
        this.gameViewModel.mainLoginShown.setValue(false);
    }

    private openTermsOfUse(): void {
        this.profileController.openGitbook(GitBookPage.TERMS_OF_USE);
    }

    private openCookiesPolicy(): void {
        this.profileController.openGitbook(GitBookPage.COOKIE_POLICY);
    }

    private openPrivacyPolicy(): void {
        this.profileController.openGitbook(GitBookPage.PRIVACY_POLICY);
    }

    private openGitbook(): void {
        this.profileController.openGitbook();
    }

    private async storeClickHandle(): Promise<void> {
        await this.profileController.showShopPopup();
    }

    private async signOutClickHandle(): Promise<void> {
        const result = await this.profileController.showSignOutPopup();
        if (result) {
            this.operationControls.complete(UserProfileResult.NONE);
        }
    }

    private async showNotification(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.to(this.notificationText, { alpha: 1, duration: 0.3 });
        animationTimeLine.to(this.notificationText, { alpha: 0, duration: 0.3, delay: 1 }, ">");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private layout(width: number, height: number): void {
        this.frame.position.set(width * 0.5, 850);
        this.closeButton.position.set(368, 1562);
        this.avatar.position.set(311, 247);
        this.leftButton.position.set(223, 311);
        this.rightButton.position.set(613, 311);
        this.levelProgressContainer.position.set(414, 674);
        this.userNicknameBox.alignmentStrategy();
        this.userNicknameBox.position.set((width - this.playerNickname.width - this.editButton.width) * 0.5, 476);
        this.walletButton.position.set(143, 800);
        this.faqButton.position.set(319, 746);
        this.supportButton.position.set(565, 791);
        this.storeButton.position.set(178, 1021);
        this.socialButton.position.set(351, 1024);
        this.volumeButton.position.set(545, 1025);
        this.underline.position.set(161, 1245);
        this.termsText.position.set(493, 1285);
        this.privacyPolicyText.position.set(161, 1285);
        this.cookiesPolicyText.position.set(161, 1348);
        this.copyUserIdText.position.set(493, 1348);
        this.signOutText.position.set(358, 1428);
        this.notificationText.position.set(width * 0.5, 1687);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.profileController.saveGuestProfile();
        this.walletButton.onUp.remove(this.onWalletClick, this);
        this.leftButton.onUp.remove(this.changeOnLastAvatar, this);
        this.rightButton.onUp.remove(this.changeOnNextAvatar, this);
        this.editButton.onUp.remove(this.editButtonClickHandle, this);
        this.closeButton.onUp.remove(this.hide, this);
        this.guestModel.propertyChanged.remove(this.onGuestModelPropertyChanged, this);
        this.playerModel.propertyChanged.remove(this.onPlayerModelPropertyChanged, this);
        this.playerNickname.onStartEditing.remove(this.onInputStartEditing, this);
        this.playerNickname.onChange.disconnect(this.onTextChanged);
        this.playerNickname.onEnter.disconnect(this.blurHandle);
        this.supportButton.onUp.remove(this.supportButtonClickHandle, this);
        this.socialButton.onUp.remove(this.socialClickHandle, this);
        this.storeButton.onUp.remove(this.storeClickHandle, this);
        this.termsText.off("pointerup", this.openTermsOfUse, this);
        this.privacyPolicyText.off("pointerup", this.openPrivacyPolicy, this);
        this.cookiesPolicyText.off("pointerup", this.openCookiesPolicy, this);
        this.signOutText.off("pointerup", this.signOutClickHandle, this);
        this.faqButton.onUp.remove(this.openGitbook, this);
        super.destroy(options);
    }
}

class NickNameTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48,
            dropShadow: true,
            dropShadowAngle: 0.8,
            dropShadowBlur: 3,
            dropShadowDistance: 3,
            dropShadowAlpha: 0.5
        }));
    }
}

class TextWithUnderlineTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#fefeff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40
        }));
    }
}
